import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useSearchParams } from 'react-router-dom';
import { Zap, Lock, BarChart3, TrendingUp, AlertTriangle, Calendar } from 'lucide-react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useSaveWithLogin } from './hooks/useSaveWithLogin';
import ResetPasswordForm from './components/auth/ResetPasswordForm';
import Navbar from './components/Navbar';
import FileUpload from './components/FileUpload';
import ExtractorInterestModal from './components/ExtractorInterestModal';
import SummaryCard from './components/SummaryCard';
import ScatterPlot from './components/ScatterPlot';
import Histogram from './components/Histogram';
import LeadTimeAnalysis from './components/LeadTimeAnalysis';
import LeadTimeChartTypeSelector from './components/LeadTimeChartTypeSelector';
import LeadTimeTypeSelector from './components/LeadTimeTypeSelector';
import LeadTimeBreakdownChart from './components/LeadTimeBreakdownChart';
import ChartTypeSelector from './components/ChartTypeSelector';
import CategorySelector from './components/CategorySelector';
import HeroSection from './components/HeroSection';
import HowItWorks from './components/HowItWorks';
import RoadmapModeSelector from './components/RoadmapModeSelector';

// Feature Flags
const ENABLE_EXTRACTOR_CTA = false;
import RoadmapCreator from './components/RoadmapCreator';
import ProtectedRoute from './components/ProtectedRoute';
import RoadmapQuickAdd from './components/RoadmapQuickAdd';
import RoadmapManager from './components/RoadmapManager';
import SaveRoadmapModal from './components/SaveRoadmapModal';
import SuccessModal from './components/SuccessModal';
import ConfirmModal from './components/ConfirmModal';
import ThroughputChart from './components/ThroughputChart';
import BloqueiosChart from './components/BloqueiosChart';
import DateRangeFilter from './components/DateRangeFilter';
import RoadmapChart from './components/RoadmapChart';
// import RoadmapChartNew from './components/RoadmapChartNew';
import { REQUIRED_HEADERS, REQUIRED_BLOQUEIOS_HEADERS, VALID_ROADMAP_STATUS, VALID_ROADMAP_LEVELS } from './utils/chartUtils';
import { excelDateToJSDate, formatDateSafe } from './utils/dateUtils';
import { validateHeaders, validateRoadmapHeaders, validateBloqueiosHeaders } from './utils/fileUtils';

import { parseCSV, parseXLSX, processRoadmapFile } from './utils/parsers';
import { useFileUpload } from './hooks/useFileUpload';
import { usePlausible } from './hooks/usePlausible';
import { useSessionTracking } from './hooks/useSessionTracking';
import { roadmapAPI, RoadmapAPIError } from './utils/roadmapAPI';
import { useErrorTracking } from './hooks/useErrorTracking';
import FeedbackWidget from './components/FeedbackWidget';
import UnsavedChangesIndicator from './components/UnsavedChangesIndicator';
import AuthorCredit from './components/AuthorCredit';
import { sendFeedback } from './utils/sendFeedback';
import LoginRequiredModal from './components/LoginRequiredModal';

// Componente para usar SearchParams dentro do Router
const MainApp = () => {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading } = useAuth();
  
  // File upload state and functions
  const fileUpload = useFileUpload();
  
  // Analytics and tracking hooks
  const plausible = usePlausible();
  const sessionTracking = useSessionTracking(plausible);
  const errorTracking = useErrorTracking(plausible);
  const { saveWithLoginSimple, showLoginModal, handleLoginSuccess, cancelSave } = useSaveWithLogin();
  
  // Estado para controlar o modal do extrator
  const [isExtractorModalOpen, setIsExtractorModalOpen] = useState(false);
  
  // Verificar se deve abrir modal de login via URL
  const shouldOpenLogin = searchParams.get('login') === 'true';
  
  // Garante que a página sempre comece no topo ao carregar/recarregar (APENAS na inicialização)
  useEffect(() => {
    // Só executa quando shouldOpenLogin for detectado via URL
    if (shouldOpenLogin) {
      // Aqui você pode adicionar lógica adicional se necessário
      if (process.env.NODE_ENV === 'development') {
        console.log('Login solicitado via URL');
      }
    }
    
    // Remove qualquer hash da URL e vai para o topo
    if (window.location.hash) {
      window.history.replaceState(null, null, window.location.pathname);
    }
    window.scrollTo(0, 0);
    
    // Track página inicial - APENAS uma vez por sessão
    if (!sessionStorage.getItem('initial_page_tracked')) {
      plausible.trackSection('home');
      sessionStorage.setItem('initial_page_tracked', 'true');
    }
  }, [shouldOpenLogin]); // Dependência corrigida
  
  const [visiblePercentiles, setVisiblePercentiles] = useState({ P50: true, P85: true, P95: true });
  const [visibleMetrics, setVisibleMetrics] = useState({ 
    avg: true, 
    p50: true, 
    p85: true, 
    p95: true 
  });
  const [visibleTypes, setVisibleTypes] = useState([]);
  const [visibleCategories, setVisibleCategories] = useState([]); // Para bloqueios
  const [data, setData] = useState([]);
  const [chartType, setChartType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null); // Nova categoria selecionada
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showChartSelector, setShowChartSelector] = useState(false);
  const [leadTimeType, setLeadTimeType] = useState(null); // Novo estado para tipo de lead time
  const [roadmapMode, setRoadmapMode] = useState(null); // Novo estado para modo do roadmap
  const [leadTimeChartType, setLeadTimeChartType] = useState('scatterplot'); // Novo estado
  const chartAreaRef = useRef(null);
  const chartRef = useRef(null); // Referência para o contêiner do gráfico

  // Estados para persistência de roadmaps
  const [currentRoadmapId, setCurrentRoadmapId] = useState(null);
  const [currentRoadmapName, setCurrentRoadmapName] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Estados para modais personalizados
  const [successModal, setSuccessModal] = useState({ show: false, title: '', message: '' });
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
  
  // Funções para modais personalizados
  const showSuccessModal = (title, message) => {
    setSuccessModal({ show: true, title, message });
  };

  const showConfirmModal = (title, message, onConfirm, variant = 'warning') => {
    return new Promise((resolve) => {
      setConfirmModal({ 
        show: true, 
        title, 
        message, 
        variant,
        onConfirm: () => {
          onConfirm();
          resolve(true);
        },
        onCancel: () => resolve(false)
      });
    });
  };

  const closeModals = () => {
    setSuccessModal({ show: false, title: '', message: '' });
    setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
  };
  
  // Estado para controlar a adição do primeiro item
  const [addingFirstItem, setAddingFirstItem] = useState(false);

  // Função wrapper para rastrear seleção de tipos de gráfico
  const handleChartTypeSelect = useCallback((type) => {
    setChartType(type);
    
    // Track o evento principal - apenas uma vez por tipo
    const chartSession = sessionStorage.getItem(`chart_${type}_selected`);
    if (!chartSession) {
      plausible.trackEvent('Chart Type Selected', { chart_type: type });
      sessionStorage.setItem(`chart_${type}_selected`, Date.now().toString());
    }
    
    // Track section change and start new section
    sessionTracking.startSection(`${type}-selection`);
    
    // REABILITAR virtual pages para chart types - são importantes
    if (type === 'leadtime') {
      plausible.trackSection('leadtime-selection');
    } else if (type === 'roadmap') {
      plausible.trackSection('roadmap-selection');
    } else if (type === 'throughput') {
      plausible.trackSection('throughput-selection');
    } else if (type === 'bloqueios') {
      plausible.trackSection('bloqueios-selection');
    }
  }, [plausible, sessionTracking]);

  // Função auxiliar para scroll com offset customizado
  const scrollToChartSelector = useCallback(() => {
    setTimeout(() => {
      // Primeiro tenta o seletor de gráficos
      let targetElement = document.getElementById('chart-selector');
      let yOffset = 0; // Offset adequado para mostrar o seletor sem esconder o contexto
      
      // Se não encontrar o seletor, usa a área de gráficos
      if (!targetElement) {
        targetElement = document.getElementById('chart-area');
        yOffset = -100; // Mesmo offset para consistência
      }
      
      if (targetElement) {
        const y = targetElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  }, []);

  // Funções para navegação por categorias
  const handleCategorySelect = useCallback((category) => {
    setSelectedCategory(category);
    
    // Track categoria selecionada
    plausible.trackEvent('Category Selected', { category });
    sessionTracking.startSection(`${category}-category`);
  }, [plausible, sessionTracking]);

  const handleBackToCategories = useCallback(() => {
    setSelectedCategory(null);
    setChartType(null);
    
    // Track volta para categorias
    plausible.trackEvent('Back to Categories');
    sessionTracking.startSection('category-selection');
  }, [plausible, sessionTracking]);

  // Handler para voltar à página inicial (logo click)
  const handleLogoClick = useCallback(() => {
    setSelectedCategory(null);
    setChartType(null);
    setLeadTimeType(null);
    setRoadmapMode(null);
    fileUpload.resetUpload();
    
    // Track volta para página inicial
    plausible.trackEvent('Logo Click - Home');
    sessionTracking.startSection('category-selection');
  }, [plausible, sessionTracking, fileUpload.resetUpload]);

  // Handler para voltar do LeadTimeTypeSelector para CategorySelector
  const handleBackToCategorySelector = useCallback(() => {
    setLeadTimeType(null);
    setChartType(null); // Limpar chartType para voltar ao CategorySelector
    
    // Track volta para seleção de chart type
    plausible.trackEvent('Back to Chart Type Selection');
    sessionTracking.startSection('chart-type-selection');
  }, [plausible, sessionTracking]);

  // Handler para voltar à seleção de gráficos
  const handleBackToChartSelection = useCallback((reason = 'manual') => {
    // Track abandonment before going back
    sessionTracking.abandonSection(reason, 25);
    
    setChartType(null);
    setSelectedCategory(null);
    setLeadTimeType(null);
    setRoadmapMode(null);
    setData([]);
    fileUpload.resetUpload();
    
    // Start new section for chart selection
    sessionTracking.startSection('chart-selector');
    scrollToChartSelector();
  }, [sessionTracking, fileUpload, scrollToChartSelector]);

  // Função para mostrar o seletor de gráfico
  const handleGetStarted = useCallback(() => {
    setShowChartSelector(true);
    
    // REDUZIR eventos para evitar rate limiting
    const getStartedSession = sessionStorage.getItem('get_started_clicked');
    if (!getStartedSession) {
      plausible.trackButtonClick('Get Started', 'homepage');
      sessionStorage.setItem('get_started_clicked', Date.now().toString());
    }
    
    // REABILITAR virtual page para chart selector
    plausible.trackSection('chart-selector');
    
    // Scroll para o seletor de gráficos com offset adequado
    setTimeout(() => {
      const targetElement = document.getElementById('chart-selector');
      if (targetElement) {
        const y = targetElement.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }, 100);
  }, [plausible]);

  // Para usuários logados, mostrar automaticamente o seletor de gráficos
  useEffect(() => {
    if (isAuthenticated && !chartType) {
      setShowChartSelector(true);
    }
  }, [isAuthenticated, chartType]);

  // Função para scroll para a seção "Como Funciona"
  const handleScrollToHowItWorks = useCallback(() => {
    const howItWorksSection = document.getElementById('como-funciona');
    if (howItWorksSection) {
      howItWorksSection.scrollIntoView({ behavior: 'smooth' });
    }
    
    // REDUZIR eventos
    const howItWorksSession = sessionStorage.getItem('how_it_works_clicked');
    if (!howItWorksSession) {
      plausible.trackButtonClick('Scroll to How It Works', 'hero_section');
      sessionStorage.setItem('how_it_works_clicked', Date.now().toString());
      
      // REABILITAR virtual page para how-it-works
      plausible.trackSection('how-it-works');
    }
  }, [plausible]);

  // Handle processed data from file upload
  const handleDataProcessed = useCallback((processedData) => {
    console.log('🎯 App.js - handleDataProcessed chamado!', {
      chartType,
      dataLength: processedData?.length,
      sampleData: processedData?.slice(0, 2)
    });
    
    const validationStartTime = Date.now();
    
    // Track data validation
    const validationResults = errorTracking.trackDataValidationError(chartType || 'throughput', processedData);
    
    // Track validation performance
    errorTracking.trackActionPerformance('data_validation', validationStartTime, {
      rowCount: processedData.length,
      complexity: processedData.length > 1000 ? 'complex' : 'simple'
    });
    
    console.log('📊 App.js - Definindo dados no estado:', processedData?.length, 'itens');
    setData(processedData);
    if (!chartType) {
      setChartType('throughput');
    }
    
    // COMENTAR eventos que geram spam durante processamento
    // Track data processing - APENAS uma vez por sessão
    const chartGenerated = sessionStorage.getItem(`chart_generated_${chartType || 'throughput'}`);
    if (!chartGenerated) {
      plausible.trackChartGeneration(chartType || 'throughput', processedData.length);
      sessionStorage.setItem(`chart_generated_${chartType || 'throughput'}`, Date.now().toString());
      
      // 📧 NOVA FUNCIONALIDADE: Notificar geração por email
      const isExampleFile = sessionStorage.getItem('is_example_file') === 'true';
      const fileInfo = JSON.parse(sessionStorage.getItem('last_file_info') || '{}');
      
      console.log('📧 [DEBUG App.js] Preparando envio de notificação após processamento de dados');
      console.log('📧 [DEBUG App.js] isExampleFile:', isExampleFile);
      console.log('📧 [DEBUG App.js] fileInfo:', fileInfo);
      
      errorTracking.notifyChartGenerated({
        type: chartType || 'throughput',
        subtype: leadTimeType || null,
        itemCount: processedData.length,
        isExample: isExampleFile,
        fileSize: fileInfo.size || null,
        fileType: fileInfo.type || null,
        processingTime: Date.now() - (fileInfo.uploadStartTime || Date.now())
      });
      
      console.log('📧 [DEBUG App.js] Chamada notifyChartGenerated realizada');
    }
    
    // REABILITAR virtual pages para chart generation - são importantes para o funil
    // Track specific chart generation virtual pages
    if (chartType === 'leadtime' && leadTimeType === 'analysis') {
      plausible.trackSection('leadtime-analysis-generated');
    } else if (chartType === 'leadtime' && leadTimeType === 'breakdown') {
      plausible.trackSection('leadtime-breakdown-generated');
    } else if (chartType === 'throughput') {
      plausible.trackSection('throughput-generated');
    } else if (chartType === 'bloqueios') {
      plausible.trackSection('bloqueios-generated');
    }
    
    sessionTracking.startSection(`${chartType || 'throughput'}-analysis`);
    
    // Scroll controlado APENAS quando dados são processados via upload
    setTimeout(() => {
      const targetElement = document.getElementById('charts-display');
      if (targetElement) {
        targetElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 300);
  }, [chartType, leadTimeType, plausible, errorTracking, sessionTracking]);

  // File upload wrapper with tracking
  const handleFileUploadWithTracking = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const startTime = Date.now();
    sessionTracking.startSection(`${chartType}-upload`);

    try {
      // COMENTAR eventos de upload para reduzir spam
      // Track file type usage
      const fileType = file.name.endsWith('.csv') ? 'csv' : 'xlsx';
      
      // APENAS track uma vez por tipo de arquivo por sessão
      const uploadSession = sessionStorage.getItem(`upload_${fileType}_${chartType}`);
      if (!uploadSession) {
        plausible.trackFileType(fileType, chartType, 'upload');
        sessionStorage.setItem(`upload_${fileType}_${chartType}`, Date.now().toString());
      }
      // plausible.trackSection(`upload-${fileType}`); // COMENTADO temporariamente

      // 📧 Marcar que está usando arquivo do usuário para notificação
      sessionStorage.setItem('is_example_file', 'false');
      sessionStorage.setItem('last_file_info', JSON.stringify({
        type: fileType,
        size: file.size,
        uploadStartTime: startTime
      }));

      // Call original file upload handler
      const result = await fileUpload.handleFileUpload(event, chartType, handleDataProcessed, leadTimeType);
      
      // Track successful upload performance
      errorTracking.trackActionPerformance('file_parse', startTime, {
        fileSize: file.size,
        fileType: fileType,
        complexity: 'simple'
      });
      
      sessionTracking.completeSection();
      return result;

    } catch (error) {
      // Track upload error
      errorTracking.trackFileUploadError(error, file, chartType);
      sessionTracking.abandonSection('error', 50);
      throw error;
    }
  }, [chartType, leadTimeType, plausible, fileUpload, handleDataProcessed, sessionTracking, errorTracking]);

  // Função wrapper para rastrear abertura do modal do extrator
  const handleExtractorModalOpen = useCallback(() => {
    setIsExtractorModalOpen(true);
    plausible.trackButtonClick('Extractor Interest', 'main_page');
  }, [plausible]);

  // Função wrapper para rastrear uso de arquivo de exemplo
  const handleExampleFileLoad = useCallback(() => {
    // COMENTAR eventos de exemplo para reduzir spam
    // Track example file usage - APENAS uma vez por tipo
    const exampleSession = sessionStorage.getItem(`example_${chartType}_used`);
    if (!exampleSession) {
      plausible.trackExampleFileUsage(chartType);
      plausible.trackFileType('xlsx', chartType, 'example');
      sessionStorage.setItem(`example_${chartType}_used`, Date.now().toString());
    }
    // plausible.trackSection('upload-xlsx'); // COMENTADO
    
    // 📧 Marcar que está usando arquivo de exemplo para notificação
    sessionStorage.setItem('is_example_file', 'true');
    sessionStorage.setItem('last_file_info', JSON.stringify({
      type: 'xlsx',
      size: null, // Arquivos de exemplo não têm tamanho
      uploadStartTime: Date.now()
    }));
    
    // Load example file
    fileUpload.loadExampleFile(chartType, handleDataProcessed, leadTimeType);
  }, [plausible, chartType, fileUpload, handleDataProcessed, leadTimeType]);

  // Função wrapper para rastrear download de arquivo de exemplo
  const handleExampleFileDownload = useCallback(() => {
    // COMENTAR download tracking temporariamente
    // Track download action - APENAS uma vez por sessão
    const downloadSession = sessionStorage.getItem(`download_example_${chartType}`);
    if (!downloadSession) {
      plausible.trackDownload('example', chartType, leadTimeType);
      sessionStorage.setItem(`download_example_${chartType}`, Date.now().toString());
    }
    // plausible.trackSection('download-example'); // COMENTADO
    plausible.trackButtonClick('Download Example', chartType);
    
    // Download file
    fileUpload.downloadExampleFile(chartType, leadTimeType);
  }, [plausible, chartType, fileUpload, leadTimeType]);

  React.useEffect(() => {
    console.log('🎯 App.js useEffect - Atualizando categorias e tipos visíveis', {
      dataLength: data.length,
      chartType: chartType,
      firstDataItem: data[0]
    });
    
    const allTypes = [...new Set(data.map(item => item['Tipo de Item']?.trim() || 'Item'))];
    console.log('📋 Tipos encontrados:', allTypes);
    setVisibleTypes(allTypes);
    
    const allCategories = [...new Set(data.map(item => item['Categoria']?.trim() || 'Padrão'))];
    console.log('🏷️ Categorias encontradas:', allCategories);
    setVisibleCategories(allCategories);
  }, [data]);

  // Virtual Page Tracking - Lead Time Type Selection
  // COMENTADO temporariamente para evitar spam
  useEffect(() => {
    // REDUZIR eventos de virtual pages
    const virtualPageSession = sessionStorage.getItem(`virtual_${leadTimeType}_${chartType}`);
    if (!virtualPageSession && leadTimeType) {
      if (leadTimeType === 'analysis') {
        plausible.trackSection('leadtime-analysis', { 
          chart_type: 'leadtime',
          analysis_type: 'analysis'
        });
      } else if (leadTimeType === 'breakdown') {
        plausible.trackSection('leadtime-breakdown', { 
          chart_type: 'leadtime',
          analysis_type: 'breakdown'
        });
      }
      sessionStorage.setItem(`virtual_${leadTimeType}_${chartType}`, Date.now().toString());
    }
  }, [leadTimeType, plausible, chartType]);

  // ========== FUNÇÕES DE PERSISTÊNCIA DE ROADMAP ==========
  
  // Estado para controlar se já houve carregamento inicial
  const [hasLoadedInitially, setHasLoadedInitially] = useState(false);
  
  // Estado para armazenar os dados salvos para comparação
  const [savedData, setSavedData] = useState([]);
  
  // Monitorar mudanças nos dados para detectar alterações não salvas
  useEffect(() => {
    if (chartType === 'roadmap' && data.length > 0) {
      // Marcar que houve carregamento inicial
      if (!hasLoadedInitially) {
        setHasLoadedInitially(true);
        return; // Não marcar como não salvo na primeira carga
      }
      
      // Só marcar como não salvo se já passou da carga inicial, tem um roadmap salvo
      // E os dados atuais são diferentes dos dados salvos
      if (hasLoadedInitially && currentRoadmapId && savedData.length > 0) {
        const currentDataString = JSON.stringify(data);
        const savedDataString = JSON.stringify(savedData);
        
        if (currentDataString !== savedDataString) {
          setHasUnsavedChanges(true);
        }
      }
    }
  }, [data, chartType, currentRoadmapId, hasLoadedInitially, savedData]);

  // Função para salvar roadmap
  const handleSaveRoadmap = useCallback(async (name) => {
    if (!data.length) {
      throw new Error('Não há dados para salvar');
    }

    setIsSaving(true);
    
    try {
      const payload = {
        name,
        items: data,
        description: '', // Pode ser expandido no futuro
        roadmapId: currentRoadmapId || null
      };
      
      const result = await roadmapAPI.save(payload);
      
      setCurrentRoadmapId(result.roadmap.id);
      setCurrentRoadmapName(name);
      setHasUnsavedChanges(false);
      
      // Armazenar os dados como salvos para comparação futura
      setSavedData([...data]);
      
      // Track save action
      plausible.trackEvent('Roadmap Saved', { 
        action: currentRoadmapId ? 'update' : 'create',
        source: roadmapMode
      });
      
      showSuccessModal(
        'Roadmap salvo com sucesso!',
        currentRoadmapId 
          ? `Roadmap "${name}" foi atualizado com sucesso!`
          : `Roadmap "${name}" foi salvo com sucesso!`
      );
    } catch (error) {
      console.error('Erro ao salvar roadmap:', error);
      const errorMessage = error instanceof RoadmapAPIError ? error.message : 'Erro ao salvar roadmap';
      throw new Error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [data, currentRoadmapId, roadmapMode, plausible]);

  // Função para atualizar roadmap existente
  const handleUpdateRoadmap = useCallback(async () => {
    if (!currentRoadmapId || !currentRoadmapName) {
      // Se não tem ID ou nome, abrir modal para criar novo
      setShowSaveModal(true);
      return;
    }

    // Atualizar usando o nome atual
    try {
      await handleSaveRoadmap(currentRoadmapName);
    } catch (error) {
      console.error('Erro ao atualizar roadmap:', error);
    }
  }, [currentRoadmapId, currentRoadmapName, handleSaveRoadmap]);

  // Wrapper para atualizar roadmap com login
  const handleUpdateRoadmapWithLogin = useCallback(() => {
    saveWithLoginSimple(handleUpdateRoadmap);
  }, [saveWithLoginSimple, handleUpdateRoadmap]);

  // Função para criar novo roadmap (sempre abre modal)
  const handleCreateNewRoadmap = useCallback(() => {
    setShowSaveModal(true);
  }, []);

  // Wrapper para criar novo roadmap com login
  const handleCreateNewRoadmapWithLogin = useCallback(() => {
    saveWithLoginSimple(handleCreateNewRoadmap);
  }, [saveWithLoginSimple, handleCreateNewRoadmap]);

  // Função estável para onSave - evita remontagem do RoadmapChart
  const stableOnSave = useMemo(() => {
    return currentRoadmapId ? handleUpdateRoadmapWithLogin : handleCreateNewRoadmapWithLogin;
  }, [currentRoadmapId, handleUpdateRoadmapWithLogin, handleCreateNewRoadmapWithLogin]);

  // Função para carregar roadmap
  const handleLoadRoadmap = useCallback(async (loadedRoadmap) => {
    // Verificar se há alterações não salvas
    if (hasUnsavedChanges) {
      const confirmResult = await new Promise((resolve) => {
        setConfirmModal({
          show: true,
          title: 'Alterações não salvas',
          message: 'Você tem alterações não salvas. Deseja continuar mesmo assim?',
          variant: 'warning',
          onConfirm: () => {
            setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
            resolve(true);
          },
          onCancel: () => {
            setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
            resolve(false);
          }
        });
      });
      
      if (!confirmResult) {
        return;
      }
    }
    
    // Carregar dados do roadmap
    setData(loadedRoadmap.items || []);
    setCurrentRoadmapId(loadedRoadmap.id);
    setCurrentRoadmapName(loadedRoadmap.name);
    setHasUnsavedChanges(false);
    
    // Armazenar os dados carregados como salvos para comparação futura
    setSavedData([...(loadedRoadmap.items || [])]);
    
    // Definir estados para permitir edição
    setChartType('roadmap');
    setRoadmapMode('create'); // Definir como create para permitir edição
    
    // Track load action
    plausible.trackEvent('Roadmap Loaded', { 
      roadmap_id: loadedRoadmap.id,
      items_count: loadedRoadmap.items?.length || 0
    });
    
    // Scroll para o gráfico
    setTimeout(() => {
      const targetElement = document.getElementById('charts-display');
      if (targetElement) {
        targetElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 300);
  }, [hasUnsavedChanges, plausible]);

  // Função para lidar com seleção de modo do roadmap
  const handleRoadmapModeSelect = useCallback((mode) => {
    if (mode === 'create') {
      // Limpar todos os dados ao criar novo roadmap
      setData([]);
      setCurrentRoadmapId(null);
      setCurrentRoadmapName('');
      setHasUnsavedChanges(false);
    }
    setRoadmapMode(mode);
  }, []);

  // Função para criar novo roadmap
  const handleNewRoadmap = useCallback(async () => {
    // Verificar se há alterações não salvas
    if (hasUnsavedChanges) {
      const confirmResult = await new Promise((resolve) => {
        setConfirmModal({
          show: true,
          title: 'Alterações não salvas',
          message: 'Você tem alterações não salvas. Deseja continuar mesmo assim?',
          variant: 'warning',
          onConfirm: () => {
            setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
            resolve(true);
          },
          onCancel: () => {
            setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
            resolve(false);
          }
        });
      });
      
      if (!confirmResult) {
        return;
      }
    }
    
    // Reset do estado
    setData([]);
    setCurrentRoadmapId(null);
    setCurrentRoadmapName('');
    setHasUnsavedChanges(false);
    setSavedData([]); // Reset dos dados salvos
    setChartType('roadmap');
    setRoadmapMode('create');
    
    // Track new roadmap action
    plausible.trackEvent('New Roadmap Started');
  }, [hasUnsavedChanges, plausible]);

  // Função para quando roadmap é excluído
  const handleRoadmapDeleted = useCallback(() => {
    // Se o roadmap atual foi excluído, limpar estado
    setCurrentRoadmapId(null);
    setCurrentRoadmapName('');
    setHasUnsavedChanges(false);
  }, []);

  // Virtual Page Tracking - Roadmap Mode Selection
  // COMENTADO temporariamente para evitar spam
  useEffect(() => {
    const roadmapSession = sessionStorage.getItem(`roadmap_${roadmapMode}_${chartType}`);
    if (!roadmapSession && roadmapMode) {
      if (roadmapMode === 'view') {
        plausible.trackSection('roadmap-view', { 
          chart_type: 'roadmap',
          mode: 'view'
        });
      } else if (roadmapMode === 'create') {
        plausible.trackSection('roadmap-create', { 
          chart_type: 'roadmap',
          mode: 'create'
        });
      }
      sessionStorage.setItem(`roadmap_${roadmapMode}_${chartType}`, Date.now().toString());
    }
  }, [roadmapMode, plausible, chartType]);

  const togglePercentile = useCallback((percentile) => {
    setVisiblePercentiles(prev => ({ ...prev, [percentile]: !prev[percentile] }));
  }, []);

  const toggleMetric = useCallback((metric) => {
    setVisibleMetrics(prev => ({ ...prev, [metric]: !prev[metric] }));
  }, []);

  // Função para lidar com dados criados via interface
  const handleRoadmapDataCreated = useCallback((createdData) => {
    setData(createdData);
    
    // Marcar como alteração não salva se já tem um roadmap carregado
    if (currentRoadmapId) {
      setHasUnsavedChanges(true);
    }
    
    // Note: We don't set fileName here as it's managed by fileUpload hook
    
    // 📧 NOVA FUNCIONALIDADE: Notificar criação de roadmap na interface (após primeiro item)
    if (createdData && createdData.length === 1) {
      // Primeiro item criado - enviar notificação
      const isFirstCreation = !sessionStorage.getItem('roadmap_interface_created');
      
      if (isFirstCreation) {
        sessionStorage.setItem('roadmap_interface_created', Date.now().toString());
        sessionStorage.setItem('is_example_file', 'false');
        sessionStorage.setItem('last_file_info', JSON.stringify({
          type: 'interface',
          size: null,
          uploadStartTime: Date.now()
        }));
        
        console.log('📧 [DEBUG App.js] Preparando envio de notificação para roadmap criado via interface');
        console.log('📧 [DEBUG App.js] createdData.length:', createdData.length);
        
        errorTracking.notifyChartGenerated({
          type: 'roadmap',
          subtype: 'interface_creation',
          itemCount: createdData.length,
          isExample: false,
          fileSize: null,
          fileType: 'interface',
          processingTime: 0 // Criação manual é instantânea
        });
        
        console.log('📧 [DEBUG App.js] Chamada notifyChartGenerated para roadmap interface realizada');
      }
    }
    
    // Scroll para a área do gráfico após criar dados
    setTimeout(() => {
      const targetElement = document.getElementById('charts-display');
      if (targetElement) {
        targetElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest'
        });
      }
    }, 300);
  }, [errorTracking, currentRoadmapId]);

  // Função para adicionar item via quick add
  const handleQuickAddItem = (newItem) => {
    setData(prevData => [...prevData, newItem]);
    // Marcar como alteração não salva
    if (currentRoadmapId) {
      setHasUnsavedChanges(true);
    }
  };

  // Função para editar item existente
  const handleEditItem = useCallback((updatedItem) => {
    setData(prevData => 
      prevData.map(item => 
        item.id === updatedItem.id ? updatedItem : item
      )
    );
    // Marcar como alteração não salva
    if (currentRoadmapId) {
      setHasUnsavedChanges(true);
    }
  }, [currentRoadmapId]);

  // Função para remover item e seus subitens
  const handleRemoveItem = useCallback((itemId) => {
    const itemToRemove = data.find(item => String(item.id) === String(itemId));
    const itemName = itemToRemove?.Nome || itemToRemove?.nome || 'Item';
    
    const newData = data.filter(item => {
      // Remove o item selecionado
      if (String(item.id) === String(itemId)) return false;
      // Remove subitens do item selecionado (usando comparação de strings)
      if (String(item.parentId) === String(itemId)) return false;
      return true;
    });
    
    setData(newData);
    
    // Marcar como alteração não salva
    if (currentRoadmapId) {
      setHasUnsavedChanges(true);
    }
    
    // Feedback visual de sucesso baseado no estado final
    if (newData.length === 0) {
      fileUpload.showMessage(`Item "${itemName}" removido! Lista limpa - você pode começar um novo roadmap.`, "success");
    } else {
      fileUpload.showMessage(`Item "${itemName}" removido com sucesso!`, "success");
    }
    
    // Limpar mensagem após 4 segundos (um pouco mais para dar tempo de ler)
    setTimeout(() => {
      fileUpload.showMessage("", "");
    }, 4000);
  }, [data, fileUpload, currentRoadmapId]);

  // Só calcula tipos e filtros de datas para leadtime/throughput
  const allTypes = useMemo(() => {
    if (chartType === 'leadtime' || chartType === 'throughput') {
      return [...new Set(data.map(item => item['Tipo de Item']?.trim() || 'Item'))];
    }
    return [];
  }, [data, chartType]);

  // Só calcula categorias para bloqueios
  const allCategories = useMemo(() => {
    if (chartType === 'bloqueios') {
      return [...new Set(data.map(item => item['Categoria']?.trim() || 'Padrão'))];
    }
    return [];
  }, [data, chartType]);

  const filteredData = useMemo(() => {
    if (chartType === 'leadtime' || chartType === 'throughput') {
      return data.filter(item => visibleTypes.includes(item['Tipo de Item']?.trim() || 'Item'));
    }
    return [];
  }, [data, visibleTypes, chartType]);

  // Estado do filtro de datas para lead time
  const closedDates = useMemo(() => {
    if (chartType === 'leadtime' || chartType === 'throughput') {
      return data.map(item => new Date(item['Closed Date'])).filter(d => !isNaN(d)).sort((a, b) => a - b);
    }
    return [];
  }, [data, chartType]);
  const minClosedDate = closedDates.length ? closedDates[0].toISOString().slice(0, 10) : '';
  const maxClosedDate = closedDates.length ? closedDates[closedDates.length - 1].toISOString().slice(0, 10) : '';
  const [ltStartDate, setLtStartDate] = useState(minClosedDate);
  const [ltEndDate, setLtEndDate] = useState(maxClosedDate);

  // Sincroniza datas do filtro sempre que os dados mudam
  React.useEffect(() => {
    if (chartType === 'leadtime' || chartType === 'throughput') {
      setLtStartDate(minClosedDate);
      setLtEndDate(maxClosedDate);
    }
  }, [minClosedDate, maxClosedDate, chartType]);

  // Dados filtrados por período para lead time
  const filteredLeadTimeData = useMemo(() => data.filter(item => {
    const d = new Date(item['Closed Date']);
    return d >= new Date(ltStartDate) && d <= new Date(ltEndDate);
  }), [data, ltStartDate, ltEndDate]);

  const summaryCards = [
    <SummaryCard key="total" label="Total de itens" value={filteredData.length} colorClass="text-gray-800 text-lg" />, 
    <SummaryCard key="min" label="Lead Time (min)" value={filteredData.length > 0 ? Math.min(...filteredData.map(i => i.leadTime)) : '-'} colorClass="text-blue-700 text-lg" />, 
    <SummaryCard key="max" label="Lead Time (max)" value={filteredData.length > 0 ? Math.max(...filteredData.map(i => i.leadTime)) : '-'} colorClass="text-red-700 text-lg" />, 
    <SummaryCard key="mean" label="Lead Time médio" value={filteredData.length > 0 ? Math.round(filteredData.reduce((acc, i) => acc + i.leadTime, 0) / filteredData.length) : '-'} colorClass="text-green-700 text-lg" />,
  ];

  // Cards de resumo para throughput
  const throughputSummaryCards = useMemo(() => {
    // O cálculo deve ser feito com base nos dados filtrados do throughput
    // (último período filtrado, não o lead time)
    // Para garantir, vamos buscar os dados do último ThroughputChart
    // Como o App não tem acesso ao filtro de datas/escala do throughput,
    // os cards devem ser calculados dentro do ThroughputChart e passados para o App
    // Solução: mover os cards de throughput para dentro do ThroughputChart e só renderizar os do lead time aqui
    return null;
  }, [data]);

  // Reinicia o estado do upload ao voltar para a tela inicial ou trocar de gráfico
  React.useEffect(() => {
    if (!chartType) {
      fileUpload.resetUpload();
      setData([]);
      setRoadmapMode(null); // Reset roadmap mode
    }
  }, [chartType, fileUpload.resetUpload]);

  return (
    <div className={`min-h-screen ${showChartSelector ? 'bg-gray-50' : 'bg-white'}`}>
      {/* Barra de navegação superior */}
      <Navbar onLogoClick={handleLogoClick} />
        
        {/* Hero Section e Como Funciona - condicionais baseadas no status de login */}
        {!chartType && !isAuthenticated && !loading && (
          <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
            <CategorySelector 
              selectedCategory={selectedCategory}
              selectedChartType={chartType}
              onCategoryChange={handleCategorySelect}
              onChartTypeChange={handleChartTypeSelect}
              onBack={handleBackToCategories}
              isAuthenticated={isAuthenticated}
            />
          </div>
        )}

        {/* Loading state durante autenticação */}
        {!chartType && loading && (
          <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando...</p>
            </div>
          </div>
        )}

        {/* Área para usuários logados sem tipo de gráfico selecionado */}
        {!chartType && isAuthenticated && !loading && (
          <div className="min-h-[calc(100vh-4rem)] bg-gray-50">
            <CategorySelector 
              selectedCategory={selectedCategory}
              selectedChartType={chartType}
              onCategoryChange={handleCategorySelect}
              onChartTypeChange={handleChartTypeSelect}
              onBack={handleBackToCategories}
              isAuthenticated={isAuthenticated}
            />
          </div>
        )}
      
      {/* Seletor de gráfico aparece quando ativado - DESABILITADO */}
      {false && showChartSelector && !chartType && (
        <div id="chart-selector" className="min-h-screen flex items-center justify-center bg-gray-50 py-4 sm:py-6 lg:py-8">
          <div className="w-full">
            <ChartTypeSelector selected={chartType} onSelect={handleChartTypeSelect} />
          
          {/* CTA para Extrator de Dados - Apenas para usuários não logados */}
          {ENABLE_EXTRACTOR_CTA && !isAuthenticated && (
            <div className="w-full max-w-4xl mx-auto px-4 mb-4 sm:mb-6 mt-4 sm:mt-6">
              <div className="p-3 sm:p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl">
                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                  <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                  <h3 className="text-sm sm:text-base font-semibold text-purple-800">
                    Dificuldade para extrair os dados?
                  </h3>
                </div>
                <p className="text-xs sm:text-sm text-purple-700 mb-2 sm:mb-3">
                  Estamos criando um extrator que coleta os dados direto do <strong>Azure DevOps</strong> e <strong>Jira</strong>, prontos para uso no Dash Simples.
                </p>
                <button
                  onClick={handleExtractorModalOpen}
                  className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white py-2 px-3 sm:px-4 rounded-lg text-sm sm:text-base font-semibold hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md flex items-center justify-center gap-2"
                >
                  <Zap size={14} className="sm:w-4 sm:h-4" />
                  Quero ser avisado quando estiver disponível
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      )}
      {chartType === 'leadtime' && !leadTimeType && (
        <div className="min-h-screen bg-gray-50 flex flex-col">
          <LeadTimeTypeSelector 
            selected={leadTimeType} 
            onSelect={setLeadTimeType} 
            onBack={handleBackToCategorySelector}
            plausible={plausible}
          />
        </div>
      )}
      {chartType === 'roadmap' && !roadmapMode && (
        <div className="min-h-screen bg-gray-50 flex flex-col">
          <RoadmapModeSelector 
            selected={roadmapMode} 
            onSelect={handleRoadmapModeSelect} 
            onBack={() => {
              setChartType(null);
              setRoadmapMode(null);
              // Mantém selectedCategory para voltar para a tela de seleção de tipos de gráfico
            }}
            plausible={plausible}
          />
        </div>
      )}
      {chartType === 'roadmap' && roadmapMode === 'saved' && (
        <div className="min-h-screen bg-gray-50 flex flex-col">
          <ProtectedRoute 
            feature="os roadmaps salvos"
            onUnauthorized={() => {
              setRoadmapMode(null);
              setChartType(null);
            }}
          >
            <RoadmapManager
              onBack={() => setRoadmapMode(null)}
              onLoadRoadmap={handleLoadRoadmap}
              onCreateNew={handleNewRoadmap}
            />
          </ProtectedRoute>
        </div>
      )}
      
      {(chartType && (
        (chartType === 'leadtime' && leadTimeType) ||
        (chartType === 'roadmap' && roadmapMode && roadmapMode !== 'saved') ||
        (chartType === 'throughput') ||
        (chartType === 'bloqueios')
      )) || (data.length > 0 && !(chartType === 'roadmap' && roadmapMode === 'saved')) ? (
        <>
          {/* Back Button - Igual à página de seleção de roadmap */}
          <div className="flex justify-center mb-1 pt-8">
            <button
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-700 hover:text-blue-600 hover:border-blue-300 font-medium transition-all duration-200 shadow-sm hover:shadow-md"
              onClick={() => {
                if (chartType === 'roadmap' && roadmapMode) {
                  setRoadmapMode(null);
                  sessionTracking.abandonSection('back_button', 50);
                } else if (chartType === 'leadtime' && leadTimeType) {
                  setLeadTimeType(null);
                  sessionTracking.abandonSection('back_button', 50);
                } else {
                  handleBackToChartSelection('back_button');
                  return; // early return to avoid the rest
                }
                setData([]);
                fileUpload.resetUpload();
              }}
              type="button"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Voltar
            </button>
          </div>
          
          <div id="chart-area" className="flex flex-col items-center justify-center">
            {chartType === 'roadmap' && roadmapMode === 'create' ? (
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  {currentRoadmapName ? `Editando: ${currentRoadmapName}` : 'Construa seu Roadmap'}
                </h3>
                <p className="text-gray-600 max-w-2xl mx-auto mb-8 text-lg leading-relaxed">
                  {currentRoadmapName 
                    ? 'Edite e gerencie os itens do seu roadmap'
                    : 'Crie, organize e gerencie os itens do seu roadmap de forma visual e interativa'
                  }
                </p>
                
                {/* Botão para adicionar primeiro item ou mensagem de estado vazio */}
                {data.length === 0 && !addingFirstItem && (
                  <div className="space-y-6">
                    <button
                      onClick={() => setAddingFirstItem(true)}
                      className="inline-flex items-center gap-3 px-8 py-4 bg-blue-600 text-white rounded-xl text-base font-semibold hover:bg-blue-700 hover:shadow-lg transition-all duration-200 transform hover:scale-105"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Adicionar Primeiro Item
                    </button>
                    <div className="text-gray-400 pt-4">
                      <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      <p className="text-sm font-medium text-gray-500">Nenhum item no roadmap</p>
                      <p className="text-xs mt-1 text-gray-400">Clique no botão acima para começar</p>
                    </div>
                  </div>
                )}
              </div>
            ) : chartType === 'roadmap' && data.length > 0 ? (
              // Para roadmaps com dados, não mostrar área de upload
              null
            ) : (
              <>
                {/* Badge do Tipo de Gráfico */}
                <div className="text-center mt-4">
                  {chartType === 'leadtime' && leadTimeType === 'analysis' && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-full mb-4">
                      <TrendingUp className="w-4 h-4 text-blue-600" />
                      <span className="text-blue-800 font-medium text-sm">Scatterplot & Histograma</span>
                    </div>
                  )}
                  
                  {chartType === 'leadtime' && leadTimeType === 'breakdown' && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-100 rounded-full mb-4">
                      <BarChart3 className="w-4 h-4 text-blue-600" />
                      <span className="text-blue-800 font-medium text-sm">Breakdown</span>
                    </div>
                  )}
                  
                  {chartType === 'throughput' && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-green-100 rounded-full mb-4">
                      <TrendingUp className="w-4 h-4 text-green-600" />
                      <span className="text-green-800 font-medium text-sm">Throughput</span>
                    </div>
                  )}
                  
                  {chartType === 'bloqueios' && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-red-100 rounded-full mb-4">
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                      <span className="text-red-800 font-medium text-sm">Bloqueios</span>
                    </div>
                  )}
                  
                  {chartType === 'roadmap' && (
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-100 rounded-full mb-4">
                      <Calendar className="w-4 h-4 text-purple-600" />
                      <span className="text-purple-800 font-medium text-sm">Roadmaps</span>
                    </div>
                  )}
                </div>

                <FileUpload 
                  onFile={handleFileUploadWithTracking} 
                  fileName={fileUpload.fileName} 
                  isProcessing={fileUpload.isProcessing} 
                  message={fileUpload.message} 
                  messageType={fileUpload.messageType} 
                  chartType={chartType}
                  leadTimeType={leadTimeType}
                  uploadKey={fileUpload.uploadKey} // Novo prop para controlar o key do input
                />
                
                {/* Banner discreto para Extrator - Apenas para usuários não logados */}
                {ENABLE_EXTRACTOR_CTA && !isAuthenticated && (
                  <div className="mt-3 mb-4">
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-400 rounded-lg p-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          <Zap className="w-5 h-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="text-sm font-semibold text-purple-800">Dificuldade para extrair os dados?</h4>
                          </div>
                          <p className="text-xs text-purple-700 mb-2 leading-relaxed">
                            Estamos criando um extrator que coleta os dados direto do <strong>Azure DevOps</strong> e <strong>Jira</strong>, prontos para uso no <strong>Dash Simples</strong>.
                          </p>
                          <button
                            onClick={handleExtractorModalOpen}
                            className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 hover:text-purple-800 transition-colors group"
                          >
                            <span>Quero ser avisado quando estiver disponível</span>
                            <svg className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Botões de Exemplo - Próximos ao upload */}
                <div className="flex gap-2 mt-4">
                  <button
                    className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold py-2 px-4 rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 text-sm"
                    type="button"
                    onClick={handleExampleFileLoad}
                  >
                    Usar arquivo de exemplo
                  </button>
                  <div className="relative group">
                    <button
                      className="bg-gray-50 hover:bg-gray-100 text-gray-700 font-semibold py-2 px-3 rounded-lg hover:text-blue-600 hover:border-blue-300 transition-all duration-200 text-sm border border-gray-200 flex items-center justify-center shadow-sm hover:shadow-md"
                      type="button"
                      onClick={handleExampleFileDownload}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </button>
                    {/* Tooltip customizado */}
                    <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10 pointer-events-none">
                      📥 Baixar arquivo de exemplo para ver a estrutura correta
                      <div className="absolute top-full right-3 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
                    </div>
                  </div>
                </div>
                
                {chartType === 'roadmap' ? (
                  <>
                    <div className="text-sm text-center text-gray-700 mt-8 space-y-3">
                      {/* Estrutura do Roadmap com Legenda */}
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-lg border border-purple-200">
                        <div className="text-sm font-semibold text-purple-800 mb-3 flex items-center justify-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Estrutura do seu arquivo:
                        </div>
                        <div className="flex items-center justify-center flex-wrap gap-2 text-xs mb-4">
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            Nome
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            Nível
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            Status
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-blue-100 border border-blue-300 px-3 py-1 rounded font-medium text-blue-700">
                            Data de Inicio
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-blue-100 border border-blue-300 px-3 py-1 rounded font-medium text-blue-700">
                            Previsão de Entrega
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-blue-100 border border-blue-300 px-3 py-1 rounded font-medium text-blue-700">
                            Data de Conclusão
                          </div>
                        </div>

                        {/* Legenda */}
                        <div className="border-t border-purple-200 pt-3 mb-3">
                          <div className="flex items-center justify-center gap-4 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                              <span className="text-gray-700">Obrigatório</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="w-3 h-3 bg-blue-100 border border-blue-300 rounded"></span>
                              <span className="text-blue-700">Opcional</span>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div className="bg-purple-50 p-2 rounded border-l-4 border-purple-400">
                            <div className="font-medium text-purple-800 mb-1">Níveis válidos:</div>
                            <div className="text-purple-700">{VALID_ROADMAP_LEVELS.join(', ')}</div>
                          </div>
                          <div className="bg-violet-50 p-2 rounded border-l-4 border-violet-400">
                            <div className="font-medium text-violet-800 mb-1">Status válidos:</div>
                            <div className="text-violet-700">{VALID_ROADMAP_STATUS.join(', ')}</div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Tabela de exemplo */}
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm font-semibold text-gray-800 mb-2 flex items-center justify-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Exemplo de estrutura:
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 px-3 py-1 text-left">Nome</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Nível</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Status</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Data de Inicio</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Previsão de Entrega</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Data de Conclusão</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border border-gray-300 px-3 py-1 whitespace-nowrap">Projeto A</td>
                                <td className="border border-gray-300 px-2 py-1 font-medium">Item</td>
                                <td className="border border-gray-300 px-2 py-1">Concluído</td>
                                <td className="border border-gray-300 px-2 py-1">02/03/2025</td>
                                <td className="border border-gray-300 px-2 py-1">29/05/2025</td>
                                <td className="border border-gray-300 px-2 py-1">12/06/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-3 py-1 whitespace-nowrap">Projeto B</td>
                                <td className="border border-gray-300 px-2 py-1 font-medium">Item</td>
                                <td className="border border-gray-300 px-2 py-1">Em Progresso</td>
                                <td className="border border-gray-300 px-2 py-1">01/06/2025</td>
                                <td className="border border-gray-300 px-2 py-1">29/09/2025</td>
                                <td className="border border-gray-300 px-2 py-1"></td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-3 py-1 pl-6 whitespace-nowrap">Atividade 1</td>
                                <td className="border border-gray-300 px-2 py-1">Subitem</td>
                                <td className="border border-gray-300 px-2 py-1">Concluído</td>
                                <td className="border border-gray-300 px-2 py-1">01/06/2025</td>
                                <td className="border border-gray-300 px-2 py-1">19/06/2025</td>
                                <td className="border border-gray-300 px-2 py-1">19/06/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-3 py-1 pl-6 whitespace-nowrap">Atividade 2</td>
                                <td className="border border-gray-300 px-2 py-1">Subitem</td>
                                <td className="border border-gray-300 px-2 py-1">Concluído</td>
                                <td className="border border-gray-300 px-2 py-1">15/06/2025</td>
                                <td className="border border-gray-300 px-2 py-1">03/07/2025</td>
                                <td className="border border-gray-300 px-2 py-1">03/07/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-3 py-1 pl-6 whitespace-nowrap">Atividade 3</td>
                                <td className="border border-gray-300 px-2 py-1">Subitem</td>
                                <td className="border border-gray-300 px-2 py-1">Em Progresso</td>
                                <td className="border border-gray-300 px-2 py-1">03/07/2025</td>
                                <td className="border border-gray-300 px-2 py-1">17/07/2025</td>
                                <td className="border border-gray-300 px-2 py-1"></td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-3 py-1 pl-6 whitespace-nowrap">Atividade 4</td>
                                <td className="border border-gray-300 px-2 py-1">Subitem</td>
                                <td className="border border-gray-300 px-2 py-1">Planejado</td>
                                <td className="border border-gray-300 px-2 py-1">20/07/2025</td>
                                <td className="border border-gray-300 px-2 py-1">21/08/2025</td>
                                <td className="border border-gray-300 px-2 py-1"></td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-3 py-1 pl-6 whitespace-nowrap">Atividade 5</td>
                                <td className="border border-gray-300 px-2 py-1">Subitem</td>
                                <td className="border border-gray-300 px-2 py-1">Planejado</td>
                                <td className="border border-gray-300 px-2 py-1">24/08/2025</td>
                                <td className="border border-gray-300 px-2 py-1">29/09/2025</td>
                                <td className="border border-gray-300 px-2 py-1"></td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-3 py-1 whitespace-nowrap">Projeto C</td>
                                <td className="border border-gray-300 px-2 py-1 font-medium">Item</td>
                                <td className="border border-gray-300 px-2 py-1">Planejado</td>
                                <td className="border border-gray-300 px-2 py-1">31/08/2025</td>
                                <td className="border border-gray-300 px-2 py-1">18/12/2025</td>
                                <td className="border border-gray-300 px-2 py-1"></td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-3 py-1 whitespace-nowrap">Projeto D</td>
                                <td className="border border-gray-300 px-2 py-1 font-medium">Item</td>
                                <td className="border border-gray-300 px-2 py-1">Cancelado</td>
                                <td className="border border-gray-300 px-2 py-1"></td>
                                <td className="border border-gray-300 px-2 py-1"></td>
                                <td className="border border-gray-300 px-2 py-1"></td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </>
                ) : chartType === 'throughput' ? (
                  <>
                    <div className="text-sm text-center text-gray-700 mt-8 space-y-3">
                      {/* Estrutura do Throughput com Legenda */}
                      <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-4 rounded-lg border border-teal-200">
                        <div className="text-sm font-semibold text-teal-800 mb-3 flex items-center justify-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Estrutura do seu arquivo:
                        </div>
                        <div className="flex items-center justify-center flex-wrap gap-2 text-xs mb-4">
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            ID
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            Tipo de Item
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            Closed Date
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                        </div>

                        {/* Legenda */}
                        <div className="border-t border-teal-200 pt-3 mb-3">
                          <div className="flex items-center justify-center gap-4 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                              <span className="text-gray-700">Obrigatório</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Tabela de exemplo para Throughput */}
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm font-semibold text-gray-800 mb-2 flex items-center justify-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Exemplo de estrutura:
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 px-2 py-1 text-left">ID</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Tipo de Item</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Closed Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-026</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-2 py-1">01/01/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-018</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-2 py-1">08/01/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-009</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-2 py-1">10/01/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-013</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-2 py-1">11/01/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-019</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-2 py-1">14/01/2025</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </>
                ) : chartType === 'leadtime' && leadTimeType === 'analysis' ? (
                  <>
                    <div className="text-sm text-center text-gray-700 mt-8 space-y-3">
                      {/* Estrutura do Lead Time Analysis com Legenda */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 rounded-lg border border-blue-200">
                        <div className="text-sm font-semibold text-blue-800 mb-2 flex items-center justify-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Estrutura do seu arquivo:
                        </div>
                        <div className="flex items-center justify-center flex-wrap gap-2 text-xs mb-3">
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            ID
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            Tipo de Item
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            Committed Date
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            Closed Date
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                        </div>

                        {/* Legenda */}
                        <div className="border-t border-blue-200 pt-2 mb-2">
                          <div className="flex items-center justify-center gap-4 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                              <span className="text-gray-700">Obrigatório</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Tabela de exemplo para Lead Time Tradicional */}
                      <div className="mt-3">
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <div className="text-sm font-semibold text-gray-800 mb-2 flex items-center justify-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Exemplo de estrutura:
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 px-2 py-1 text-left">ID</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Tipo de Item</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Committed Date</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Closed Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-026</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-2 py-1">11/12/2024</td>
                                <td className="border border-gray-300 px-2 py-1">01/01/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-018</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-2 py-1">30/12/2024</td>
                                <td className="border border-gray-300 px-2 py-1">08/01/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-009</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-2 py-1">19/11/2024</td>
                                <td className="border border-gray-300 px-2 py-1">10/01/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-013</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-2 py-1">03/12/2024</td>
                                <td className="border border-gray-300 px-2 py-1">11/01/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-019</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-2 py-1">14/01/2025</td>
                                <td className="border border-gray-300 px-2 py-1">14/01/2025</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>

                        {/* Explicação Visual do Cálculo - Versão Melhorada */}
                        <div className="mt-3 text-xs text-gray-600 bg-gradient-to-r from-blue-50 to-indigo-50 p-5 rounded-lg border border-blue-200">
                          <div className="text-sm font-semibold text-blue-800 mb-4 flex items-center justify-center gap-2">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Como é calculado o Lead Time
                          </div>
                          
                          <div className="text-xs text-blue-700 text-center mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <span className="font-semibold text-sm">⏱️ Fórmula:</span><br/>
                            <span className="text-blue-600">Lead Time = Data de Entrega - Data de Compromisso</span>
                          </div>
                          
                          {/* Exemplo Visual com Timeline */}
                          <div className="bg-white p-4 rounded-lg border border-blue-200 mb-4 shadow-sm">
                            <div className="text-blue-800 font-semibold mb-4 text-center text-sm">📋 US-026 - Exemplo Prático</div>
                            
                            {/* Timeline Visual */}
                            <div className="relative mb-4">
                              {/* Pontos da timeline */}
                              <div className="relative flex justify-between items-center">
                                {/* Início - Committed Date */}
                                <div className="flex flex-col items-center bg-white p-2 rounded-lg border-2 border-blue-400 shadow-sm">
                                  <div className="w-4 h-4 bg-blue-500 rounded-full mb-2 animate-pulse"></div>
                                  <div className="text-center">
                                    <div className="font-semibold text-blue-700 text-xs">INÍCIO</div>
                                    <div className="text-blue-600 text-xs font-medium">Committed</div>
                                    <div className="bg-blue-100 px-2 py-1 rounded text-blue-800 font-mono text-xs">11/12/2024</div>
                                  </div>
                                </div>
                                
                                {/* Linha do tempo com badge no centro */}
                                <div className="flex-1 relative flex items-center justify-center mx-4">
                                  {/* Linha dividida em duas partes */}
                                  <div className="absolute left-0 right-0 flex items-center">
                                    <div className="flex-1 h-1 bg-gradient-to-r from-blue-300 to-green-200 rounded-l-full"></div>
                                    <div className="flex-1 h-1 bg-gradient-to-r from-green-200 to-green-400 rounded-r-full"></div>
                                  </div>
                                  
                                  {/* Badge centralizado */}
                                  <div className="relative z-10 bg-blue-600 px-5 py-2 rounded-full text-white font-bold shadow-lg flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span className="text-sm">21 dias</span>
                                  </div>
                                </div>
                                
                                {/* Fim - Closed Date */}
                                <div className="flex flex-col items-center bg-white p-2 rounded-lg border-2 border-green-400 shadow-sm">
                                  <div className="w-4 h-4 bg-green-500 rounded-full mb-2"></div>
                                  <div className="text-center">
                                    <div className="font-semibold text-green-700 text-xs">ENTREGA</div>
                                    <div className="text-green-600 text-xs font-medium">Closed</div>
                                    <div className="bg-green-100 px-2 py-1 rounded text-green-800 font-mono text-xs">01/01/2025</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : chartType === 'leadtime' && leadTimeType === 'breakdown' ? (
                  <>
                    <div className="text-sm text-center text-gray-700 mt-8 space-y-3">
                      {/* Diagrama de Fluxo Visual com Legenda */}
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                        <div className="text-sm font-semibold text-blue-800 mb-3 flex items-center justify-center gap-2">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Estrutura do seu arquivo:
                        </div>
                        <div className="flex items-center justify-center flex-wrap gap-2 text-xs mb-4">
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            ID
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            Tipo de Item
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-blue-200 border border-blue-400 px-3 py-1 rounded-lg font-semibold text-blue-800 relative shadow-sm">
                            Sua Etapa 1
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                          <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-blue-200 border border-blue-400 px-3 py-1 rounded-lg font-semibold text-blue-800 relative shadow-sm">
                            Sua Etapa 2
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                          <svg className="w-4 h-4 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-blue-200 border border-blue-400 px-2 py-1 rounded-lg text-blue-800 font-medium shadow-sm">
                            ...
                          </div>
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                          </svg>
                          <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                            Closed Date
                            <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                          </div>
                        </div>

                        {/* Legenda */}
                        <div className="border-t border-blue-200 pt-3 mb-3">
                          <div className="flex items-center justify-center gap-6 text-xs">
                            <div className="flex items-center gap-1">
                              <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                              <span className="text-gray-700">Obrigatório</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <span className="w-4 h-3 bg-blue-200 border border-blue-400 rounded shadow-sm"></span>
                              <span className="text-blue-700">Suas etapas do processo</span>
                            </div>
                          </div>
                        </div>

                        <div className="text-xs text-blue-700 text-center space-y-2">
                          <div>
                            <span className="font-medium">💡 Personalize:</span> Use os nomes das suas etapas reais (ex: "Em Desenvolvimento", "Code Review", "Testes")
                          </div>
                          <div className="text-gray-600 text-xs">
                            <span className="font-medium">ℹ️ Lembre-se:</span> A primeira etapa marca o início do lead time • Cada data representa quando o item entrou naquela etapa
                          </div>
                        </div>
                      </div>

                      {/* Tabela de exemplo para Lead Time Breakdown */}
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm font-semibold text-gray-800 mb-2 flex items-center justify-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                          Exemplo de estrutura:
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs border-collapse">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 px-2 py-1 text-left">ID</th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Tipo de Item</th>
                                <th className="border border-gray-300 px-1 py-1 text-left bg-blue-200 text-blue-800 font-semibold">
                                  Em Desenvolvimento
                                </th>
                                <th className="border border-gray-300 px-1 py-1 text-left bg-blue-200 text-blue-800 font-semibold">
                                  Liberado para Testes
                                </th>
                                <th className="border border-gray-300 px-1 py-1 text-left bg-blue-200 text-blue-800 font-semibold">
                                  Em Testes
                                </th>
                                <th className="border border-gray-300 px-1 py-1 text-left bg-blue-200 text-blue-800 font-semibold">
                                  Pronto para Produção
                                </th>
                                <th className="border border-gray-300 px-2 py-1 text-left">Closed Date</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-026</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">12/12/2024</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">18/12/2024</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">24/12/2024</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">31/12/2024</td>
                                <td className="border border-gray-300 px-2 py-1">01/01/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-018</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">31/12/2024</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">02/01/2025</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">04/01/2025</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">07/01/2025</td>
                                <td className="border border-gray-300 px-2 py-1">08/01/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-009</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">20/11/2024</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">06/12/2024</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">23/12/2024</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">09/01/2025</td>
                                <td className="border border-gray-300 px-2 py-1">10/01/2025</td>
                              </tr>
                              <tr>
                                <td className="border border-gray-300 px-2 py-1">US-019</td>
                                <td className="border border-gray-300 px-2 py-1">User Story</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">15/12/2024</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">24/12/2024</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">03/01/2025</td>
                                <td className="border border-gray-300 px-1 py-1 bg-blue-50 text-blue-800 font-medium">13/01/2025</td>
                                <td className="border border-gray-300 px-2 py-1">14/01/2025</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        
                        {/* Explicação do cálculo mais visual */}
                        <div className="mt-3 text-xs text-gray-600 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
                          <div className="text-sm font-semibold text-blue-800 mb-3 flex items-center justify-center gap-2">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6l-6 10v-10zm6-2h2a2 2 0 012 2v12a2 2 0 01-2 2H7a2 2 0 01-2-2V7a2 2 0 012-2h2m6 0V3a1 1 0 00-1-1h-4a1 1 0 00-1 1v2m6 0V3a1 1 0 00-1-1h-4a1 1 0 00-1 1v2" />
                            </svg>
                            Como é calculado o tempo de cada etapa:
                          </div>
                          
                          <div className="bg-white p-3 rounded border border-blue-200 mb-3">
                            <div className="text-blue-800 font-semibold mb-2">📋 US-026 - Exemplo Visual:</div>
                            
                            {/* Timeline Visual */}
                            <div className="space-y-3">
                              {/* Em Desenvolvimento */}
                              <div className="flex items-center gap-3">
                                <div className="text-xs font-medium text-blue-800 w-32 text-right">Em Desenvolvimento:</div>
                                <div className="flex-1 bg-gray-200 rounded-full h-3 relative overflow-hidden">
                                  <div className="bg-gradient-to-r from-blue-400 to-blue-500 h-3 rounded-full transition-all duration-1000 ease-out" style={{width: '30%'}}></div>
                                  <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-blue-900">6 dias</div>
                                </div>
                                <div className="text-xs text-gray-600 w-24">12/12 → 18/12</div>
                              </div>
                              
                              {/* Liberado para Testes */}
                              <div className="flex items-center gap-3">
                                <div className="text-xs font-medium text-indigo-800 w-32 text-right">Liberado p/ Testes:</div>
                                <div className="flex-1 bg-gray-200 rounded-full h-3 relative overflow-hidden">
                                  <div className="bg-gradient-to-r from-indigo-400 to-indigo-500 h-3 rounded-full transition-all duration-1000 ease-out delay-200" style={{width: '30%'}}></div>
                                  <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-indigo-900">6 dias</div>
                                </div>
                                <div className="text-xs text-gray-600 w-24">18/12 → 24/12</div>
                              </div>
                              
                              {/* Em Testes */}
                              <div className="flex items-center gap-3">
                                <div className="text-xs font-medium text-purple-800 w-32 text-right">Em Testes:</div>
                                <div className="flex-1 bg-gray-200 rounded-full h-3 relative overflow-hidden">
                                  <div className="bg-gradient-to-r from-purple-400 to-purple-500 h-3 rounded-full transition-all duration-1000 ease-out delay-400" style={{width: '35%'}}></div>
                                  <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-purple-900">7 dias</div>
                                </div>
                                <div className="text-xs text-gray-600 w-24">24/12 → 31/12</div>
                              </div>
                              
                              {/* Pronto para Produção */}
                              <div className="flex items-center gap-3">
                                <div className="text-xs font-medium text-violet-800 w-32 text-right">Pronto p/ Produção:</div>
                                <div className="flex-1 bg-gray-200 rounded-full h-3 relative overflow-hidden">
                                  <div className="bg-gradient-to-r from-violet-400 to-violet-500 h-3 rounded-full transition-all duration-1000 ease-out delay-600" style={{width: '5%'}}></div>
                                  <div className="absolute inset-0 flex items-center justify-center text-xs font-bold text-violet-900">1 dia</div>
                                </div>
                                <div className="text-xs text-gray-600 w-24">31/12 → 01/01</div>
                              </div>
                            </div>
                            
                            {/* Total Lead Time */}
                            <div className="mt-4 pt-3 border-t border-blue-200">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <span className="font-semibold text-blue-800">Total Lead Time:</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="bg-gradient-to-r from-blue-400 to-blue-500 px-3 py-1 rounded-full text-white font-bold text-sm">
                                    20 dias
                                  </div>
                                  <span className="text-xs text-gray-600">(6 + 6 + 7 + 1)</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ) : chartType === 'bloqueios' ? (
                  <div className="text-sm text-center text-gray-700 mt-8 space-y-3">
                    {/* Estrutura de Bloqueios com Legenda */}
                    <div className="bg-gradient-to-r from-red-50 to-orange-50 p-4 rounded-lg border border-red-200">
                      <div className="text-sm font-semibold text-red-800 mb-3 flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Estrutura do seu arquivo:
                      </div>
                      <div className="flex items-center justify-center flex-wrap gap-2 text-xs mb-4">
                        <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                          ID
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                        </div>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                          Data de Criação
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                        </div>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                          Data de Conclusão
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                        </div>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        <div className="bg-blue-100 border border-blue-300 px-3 py-1 rounded font-medium text-blue-700">
                          Categoria
                        </div>
                      </div>

                      {/* Legenda */}
                      <div className="border-t border-red-200 pt-3">
                        <div className="flex items-center justify-center gap-4 text-xs">
                          <div className="flex items-center gap-1">
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            <span className="text-gray-700">Obrigatório</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                            <span className="text-blue-700">Opcional</span>
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-600 text-center">
                          ⚠️ <strong>Importante:</strong> Se a coluna "Categoria" estiver presente, ela deve ser a 4ª coluna
                        </div>
                      </div>
                    </div>
                    
                    {/* Tabela de exemplo para Bloqueios */}
                    <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                      <div className="text-sm font-semibold text-gray-800 mb-2 flex items-center justify-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Exemplo de estrutura:
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-gray-100">
                              <th className="border border-gray-300 px-2 py-1 text-left">ID</th>
                              <th className="border border-gray-300 px-2 py-1 text-left">Data de Criação</th>
                              <th className="border border-gray-300 px-2 py-1 text-left">Data de Conclusão</th>
                              <th className="border border-gray-300 px-2 py-1 text-left">Categoria</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td className="border border-gray-300 px-2 py-1">BLQ-001</td>
                              <td className="border border-gray-300 px-2 py-1">23/03/2025</td>
                              <td className="border border-gray-300 px-2 py-1">06/04/2025</td>
                              <td className="border border-gray-300 px-2 py-1">Dependência com Fornecedor</td>
                            </tr>
                            <tr>
                              <td className="border border-gray-300 px-2 py-1">BLQ-002</td>
                              <td className="border border-gray-300 px-2 py-1">11/01/2025</td>
                              <td className="border border-gray-300 px-2 py-1">29/01/2025</td>
                              <td className="border border-gray-300 px-2 py-1">Indisponibilidade de Ambiente</td>
                            </tr>
                            <tr>
                              <td className="border border-gray-300 px-2 py-1">BLQ-003</td>
                              <td className="border border-gray-300 px-2 py-1">06/03/2025</td>
                              <td className="border border-gray-300 px-2 py-1">23/03/2025</td>
                              <td className="border border-gray-300 px-2 py-1">Dependência com Fornecedor</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-center text-gray-700 mt-8 space-y-3">
                    {/* Estrutura do seu arquivo com Legenda */}
                    <div className="bg-gradient-to-r from-gray-50 to-slate-50 p-4 rounded-lg border border-gray-200">
                      <div className="text-sm font-semibold text-gray-800 mb-3 flex items-center justify-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Estrutura do seu arquivo:
                      </div>
                      <div className="flex items-center justify-center flex-wrap gap-2 text-xs mb-4">
                        <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                          ID
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                        </div>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                          Tipo de Item
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                        </div>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                          Committed Date
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                        </div>
                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                        </svg>
                        <div className="bg-gray-100 border border-gray-300 px-3 py-1 rounded font-medium relative">
                          Closed Date
                          <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                        </div>
                      </div>

                      {/* Legenda */}
                      <div className="border-t border-gray-200 pt-3">
                        <div className="flex items-center justify-center gap-4 text-xs">
                          <div className="flex items-center gap-1">
                            <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                            <span className="text-gray-700">Obrigatório</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
              </>
            )}
          </div>
        </>
      ) : null}
      {chartType === 'roadmap' && roadmapMode === 'create' && data.length === 0 && (
        <div className="w-full">
          <ProtectedRoute 
            feature="o editor de roadmap"
            onUnauthorized={() => {
              setRoadmapMode(null);
              setChartType(null);
            }}
          >
            <RoadmapCreator 
              addingFirstItem={addingFirstItem}
              setAddingFirstItem={setAddingFirstItem}
              onDataCreated={handleRoadmapDataCreated} 
            />
          </ProtectedRoute>
        </div>
      )}
      
      {/* Debug log principal de renderização */}
      {console.log('🔍 App.js - Estado antes de renderizar charts:', {
        chartType,
        dataLength: data?.length,
        hasData: data?.length > 0,
        condition: chartType && data.length > 0,
        allCategories: allCategories?.length,
        visibleCategories: visibleCategories?.length
      })}
      
      {chartType && data.length > 0 && (
        <div id="charts-display" ref={chartAreaRef} className="w-full">
          {/* Debug log para renderização de charts */}
          {console.log('🎯 App.js - Renderizando charts:', {
            chartType,
            dataLength: data.length,
            hasData: data.length > 0,
            sampleData: data.slice(0, 2)
          })}
          {chartType === 'leadtime' && leadTimeType === 'analysis' ? (
            <>
              {leadTimeChartType === 'scatterplot' ? (
                <ScatterPlot
                  data={data}
                  allTypes={allTypes}
                  visibleTypes={visibleTypes}
                  visiblePercentiles={visiblePercentiles}
                  setVisibleTypes={setVisibleTypes}
                  togglePercentile={togglePercentile}
                  leadTimeChartType={leadTimeChartType}
                  setLeadTimeChartType={setLeadTimeChartType}
                  plausible={plausible}
                />
              ) : leadTimeChartType === 'histogram' ? (
                <Histogram
                  data={data}
                  allTypes={allTypes}
                  visibleTypes={visibleTypes}
                  visiblePercentiles={visiblePercentiles}
                  setVisibleTypes={setVisibleTypes}
                  togglePercentile={togglePercentile}
                  leadTimeChartType={leadTimeChartType}
                  setLeadTimeChartType={setLeadTimeChartType}
                  plausible={plausible}
                />
              ) : leadTimeChartType === 'outros' ? (
                <LeadTimeAnalysis
                  data={data}
                  allTypes={allTypes}
                  visibleTypes={visibleTypes}
                  visiblePercentiles={visiblePercentiles}
                  visibleMetrics={visibleMetrics}
                  setVisibleTypes={setVisibleTypes}
                  togglePercentile={togglePercentile}
                  toggleMetric={toggleMetric}
                  leadTimeChartType={leadTimeChartType}
                  setLeadTimeChartType={setLeadTimeChartType}
                  plausible={plausible}
                />
              ) : null}
              {leadTimeChartType !== 'outros' && (
                <div className="flex flex-row flex-nowrap w-full items-center gap-2 mt-6 max-w-4xl mx-auto pb-2 px-1 snap-x min-w-0 overflow-x-auto whitespace-nowrap">
                  {summaryCards}
                </div>
              )}
            </>
          ) : chartType === 'leadtime' && leadTimeType === 'breakdown' ? (
            <LeadTimeBreakdownChart 
              data={data} 
              minDate={minClosedDate}
              maxDate={maxClosedDate}
              plausible={plausible}
            />
          ) : chartType === 'throughput' ? (
            <ThroughputChart 
              data={data} 
              allTypes={allTypes}
              visibleTypes={visibleTypes}
              setVisibleTypes={setVisibleTypes}
              plausible={plausible}
            />
          ) : chartType === 'bloqueios' ? (
            <BloqueiosChart 
              data={data} 
              allCategories={allCategories}
              visibleCategories={visibleCategories}
              setVisibleCategories={setVisibleCategories}
              plausible={plausible}
            />
          ) : chartType === 'roadmap' ? (
            <RoadmapChart 
              data={data}
              onEditItem={handleEditItem}
              onRemoveItem={handleRemoveItem}
              plausible={plausible}
              hasUnsavedChanges={hasUnsavedChanges}
              currentRoadmapName={currentRoadmapName}
              currentRoadmapId={currentRoadmapId}
              onSave={stableOnSave}
              isSaving={isSaving}
              onMyRoadmaps={isAuthenticated ? () => {
                setRoadmapMode('saved');
                // Scroll para a seção de roadmaps salvos
                setTimeout(() => {
                  window.scrollTo({ 
                    top: 0, 
                    behavior: 'smooth' 
                  });
                }, 100);
              } : undefined}
            />
          ) : null}
          
          {/* Espaçamento inferior para compensar a barra fixa */}
          {chartType === 'roadmap' && (roadmapMode === 'create' || roadmapMode === 'upload') && data.length > 0 && (
            <div className="h-16"></div>
          )}
        </div>
      )}
      {data.length > 0 && (
        <div className="flex justify-center mt-16">
          <AuthorCredit variant="minimal" size="sm" />
        </div>
      )}
    {/* Widget de feedback global */}
    <FeedbackWidget onSend={sendFeedback} />
    
    {/* Modal do Extrator de Dados */}
    <ExtractorInterestModal
      isOpen={isExtractorModalOpen}
      onClose={() => setIsExtractorModalOpen(false)}
      plausible={plausible}
    />

    {/* Modais e indicadores de roadmap */}
    <SaveRoadmapModal
      isOpen={showSaveModal}
      onClose={() => setShowSaveModal(false)}
      onSave={handleSaveRoadmap}
      currentName=""
      isUpdating={false}
    />

    {/* Modais personalizados */}
    <SuccessModal
      isOpen={successModal.show}
      onClose={closeModals}
      title={successModal.title}
      message={successModal.message}
    />

    <ConfirmModal
      isOpen={confirmModal.show}
      onClose={() => {
        if (confirmModal.onCancel) confirmModal.onCancel();
        closeModals();
      }}
      onConfirm={confirmModal.onConfirm}
      title={confirmModal.title}
      message={confirmModal.message}
      variant={confirmModal.variant}
    />

    {/* Indicador de mudanças não salvas */}
    <UnsavedChangesIndicator
      hasUnsavedChanges={hasUnsavedChanges}
      onSave={stableOnSave}
      roadmapName={currentRoadmapName}
      isCurrentlySaving={isSaving}
    />

    {/* Modal de login para salvar roadmap */}
    <LoginRequiredModal
      isOpen={showLoginModal}
      onClose={cancelSave}
      feature="saveRoadmap"
      onLoginSuccess={handleLoginSuccess}
      onRegisterSuccess={handleLoginSuccess}
    />

    {/* Barra fixa inferior com botões de adicionar itens */}
    {chartType === 'roadmap' && (roadmapMode === 'create' || roadmapMode === 'upload') && data.length > 0 && (
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg backdrop-blur-sm bg-white/95">
        <div className="w-full max-w-6xl mx-auto px-4 py-3">
          <div className="flex items-center justify-center">
            <RoadmapQuickAdd 
              onItemAdded={handleQuickAddItem} 
              existingItems={data}
              onEditItem={handleEditItem}
              onRemoveItem={handleRemoveItem}
              showItemsList={false} // Não mostrar a lista de itens
              onSave={stableOnSave}
              isSaving={isSaving}
              currentRoadmapId={currentRoadmapId}
              hasUnsavedChanges={hasUnsavedChanges}
              currentRoadmapName={currentRoadmapName}
              compactMode={true} // Usar layout compacto para a barra fixa
            />
          </div>
        </div>
      </div>
    )}
      </div>
  );
};

// Componente principal App com roteamento
const AgileMetricsApp = () => {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/reset-password" element={<ResetPasswordForm />} />
          <Route path="/" element={<MainApp />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
};

export default AgileMetricsApp;